<template>
    <div class="company-type-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Company Type</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div class="form-wrapper" v-else>
                    <div class="input-container">
                        <app-input 
                            v-model="companyType.name" 
                            label="Name" 
                            placeholder="Name"  
                            :isValid="!submitted || $v.companyType.name.required"
                            :error="`Name is required`"
                        />
                    </div>
                    <div class="company-type-color-wrapper">
                        <p class="company-type-color-label">Color</p>
                        <div class="company-type-color"  ref="color" :style="{ 'background-color': companyType.color }" @click="showColorPicker"></div>
                        <color-picker v-if="isColorPickerShown" v-off-click="{ handler: 'hideColorPicker', exclude: ['color'] }" class="company-type-color-picker" @input="setCompanyTypeColor($event)" :value="companyType.color" />
                    </div>
                </div>
            </div> 
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { Sketch } from 'vue-color';
import { required} from 'vuelidate/lib/validators';

export default {
    name: 'company-type-widget',

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'loading-screen-svg': LoadingScreenSvg,
        'color-picker': Sketch,
    },

    data() {
        return {
            submitted: false,
            isLoading: true,
            companyType: {
                id: this.widget.companyTypeId,
                clientId: this.widget.clientId,
                name: null,
                color: '',
            },
            isColorPickerShown: false,
        }
    },

    validations: {
        companyType: {
            name: {
                required
            },
        },
    },

    methods: {
        showColorPicker() {
            this.isColorPickerShown = true;
        },
        
        hideColorPicker() {
            this.isColorPickerShown = false;
        },

        setCompanyTypeColor(color) {
            this.companyType.color = color.hex;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            if(this.companyType.id) {
                this.patch();
            } 
            else {
                this.post();
            }
        },

        patch() {
            this.isLoading = true;

            var changes = [{
                'name': 'Name',
                'value': this.companyType.name
            }, {
                'name': 'Color',
                'value': this.companyType.color
            }];

            this.$api
                .patch(`/api/CompanyTypes/${ this.companyType.id }`, changes)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your company type has been successfully updated.');
                    EventBus.$emit('company-type:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while updating your company type.', { error });
                });
        },

        post() {
            this.isLoading = true;

            var companyType = {
                Name: this.companyType.name,
                Color: this.companyType.color,
                ClientId: this.companyType.clientId,
            };

            this.$api
                .post(`/api/CompanyTypes`, companyType)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your company type has been successfully created.');
                    EventBus.$emit('company-type:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while creating your company type.', { error });
                });
        },

        getCompanyType() {
            this.isLoading = true;

            this.$api
                .get(`/service/CompanyTypes?$filter=Id eq ${ this.companyType.id }`)
                .then(response => {
                    this.companyType.id = response.data.value[0].Id;
                    this.companyType.name = response.data.value[0].Name;
                    this.companyType.color = response.data.value[0].Color;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your company type.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },

    created() {
        if (this.companyType.id) {
            this.getCompanyType();
        }
        else {
            this.isLoading = false;
        }
    }
}
</script>

<style>
.company-type-widget .form-wrapper {
    display: flex;
    flex-direction: column;
}

.company-type-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.company-type-widget .widget-contents-container .transparent-btn:hover {
    background: #E1E0E5;
}

.company-type-widget .app-input {
    margin-bottom: 16px;
}

.company-type-widget .company-type-color-wrapper {
    position: relative;
    width: 30px;
    margin-right: 15px;
}

.company-type-widget .company-type-color-wrapper .company-type-color {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    border: 2px solid #E9E9EF;
}

.company-type-widget .company-type-color-wrapper .company-type-color-picker {
    position: absolute;
    top: 15px;
    left: 50%;
    z-index: 1001;
}

.company-type-widget .company-type-color-label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
}
</style>